import React, { useEffect, useMemo, useState } from 'react';
import Toolbar from '@mui/material/Toolbar';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@emotion/react';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import clsx from 'clsx';

import Logo from '../Logo';
import { matchPath, useLocation } from 'react-router';
import { t } from 'i18next';
import { Link, useHistory } from 'react-router-dom';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import SidebarToggleHandler from '../../../../../@coremat/CmtLayouts/Vertical/SidebarToggleHandler';
import { useDealer } from 'services/Data/DealerServices';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'redux/Account/AccountAction';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    paddingLeft: 16,
    paddingRight: 16,
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      minHeight: 72,
      paddingLeft: 24,
      paddingRight: 24,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 56,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      gap: theme.spacing(1),
    },
  },
  leftSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
    },
  },
  titleWrapper: {
    minWidth: 260,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      minWidth: 0,
    },
  },
  brandSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5),
    },
  },
  menuWrapper: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    minWidth: 0,
  },
  menu: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'nowrap',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      justifyContent: 'flex-start',
      overflowX: 'auto',
      paddingBottom: theme.spacing(0.5),
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  },
  menuButton: {
    color: alpha(theme.palette.common.white, 0.7),
    borderRadius: 24,
    letterSpacing: '0.08rem',
    fontFamily: 'T Star Pro',
    paddingInline: theme.spacing(2.5),
    position: 'relative',
    '&::after': {
      content: "''",
      position: 'absolute',
      bottom: theme.spacing(0.5),
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 3,
      borderRadius: 999,
      transition: 'width 0.3s ease, background-color 0.3s ease',
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.12),
      '&::after': {
        width: `calc(100% - (${theme.spacing(2.5)} * 2) + ${theme.spacing(1)})`,
        backgroundColor: alpha(theme.palette.secondary.main, 0.8),
      },
    },
  },
  menuButtonActive: {
    color: theme.palette.secondary.main,
    '&::after': {
      width: `calc(100% - (${theme.spacing(2.5)} * 2) + ${theme.spacing(1)})`,
      backgroundColor: theme.palette.secondary.main,
    },
  },
  controls: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.2),
    borderLeft: `solid 1px ${alpha(theme.palette.common.dark, 0.15)}`,
    paddingLeft: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      borderLeft: 'none',
      paddingLeft: 0,
    },
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: theme.spacing(0.5),
    },
  },
  rightSection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flex: 'initial',
      gap: theme.spacing(0.5),
    },
  },
  toggleButton: {
    color: theme.palette.common.white,
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      paddingRight: theme.spacing(1),
    },
  },
}));

const CertificationToolHeader = () => {
  const isLoggedIn = useSelector(state => state.AccountReducer?.isLoggedIn);

  if (!isLoggedIn) {
    return null;
  }

  return <CertificationToolHeaderContent />;
};

const CertificationToolHeaderContent = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [certThemeMode, setCertThemeMode] = useState('light');
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState(null);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: dealer } = useDealer();

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const canShowBackToPortal = dealer?.hasPortalAccess === true;
  const showBackToPortalInHeader = canShowBackToPortal && !isMdDown;
  const isAccountMenuOpen = Boolean(accountMenuAnchorEl);

  useEffect(() => {
    const syncCertTheme = () => {
      if (typeof window === 'undefined') return;
      const stored = window.localStorage.getItem('certificationThemeMode');
      setCertThemeMode(stored === 'dark' ? 'dark' : 'light');
    };

    syncCertTheme();

    window.addEventListener('cert-theme-mode-changed', syncCertTheme);
    window.addEventListener('storage', syncCertTheme);
    return () => {
      window.removeEventListener('cert-theme-mode-changed', syncCertTheme);
      window.removeEventListener('storage', syncCertTheme);
    };
  }, []);
  const menuItems = useMemo(
    () => [
      {
        label: t('certNav.overview')?.toUpperCase(),
        path: '/dealer/certification',
        dataTour: 'navCertificationOverview',
        matchPath: '/dealer/certification',
      },
      {
        label: t('certNav.submissions')?.toUpperCase(),
        path: '/dealer/certification/submissions',
        dataTour: 'navCertification',
        matchPath: '/dealer/certification/submissions',
      },
      {
        label: t('certNav.userGuide')?.toUpperCase(),
        path: '/dealer/certification/user-manual',
        dataTour: 'navGuide',
        matchPath: '/dealer/certification/user-manual',
      },
    ],
    [t],
  );
  const activePath = useMemo(() => {
    const accountMatch = matchPath(location.pathname, {
      path: '/dealer/certification/account',
      exact: false,
      sensitive: false,
    });

    if (accountMatch) {
      return null;
    }

    return menuItems.reduce((bestMatch, item) => {
      const match = matchPath(location.pathname, {
        path: item.matchPath,
        exact: false,
        sensitive: false,
      });

      if (!match) {
        return bestMatch;
      }

      if (!bestMatch || item.matchPath.length > bestMatch.length) {
        return item.matchPath;
      }

      return bestMatch;
    }, null);
  }, [location.pathname, menuItems]);

  const handleAccountMenuToggle = event => {
    if (accountMenuAnchorEl) {
      setAccountMenuAnchorEl(null);
      return;
    }

    setAccountMenuAnchorEl(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchorEl(null);
  };

  const handleAccountNavigation = () => {
    handleAccountMenuClose();
    history.push('/dealer/certification/account');
  };

  const handleLogout = () => {
    handleAccountMenuClose();
    dispatch(logout());
  };

  return (
    <Toolbar className={classes.root}>
      <Box className={classes.leftSection}>
        <Box className={classes.brandSection}>
          <Box className={classes.toggleButton}>
            <SidebarToggleHandler edge="start" color="inherit" aria-label="menu" />
          </Box>
        </Box>
        <Box className={classes.titleWrapper}>
          <Box
            component={Link}
            to="/dealer/certification"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', md: 'flex-start' },
              flexWrap: 'wrap',
              columnGap: 1,
              rowGap: 0.5,
              textDecoration: 'none',
            }}>
            <Box
              component="img"
              src="/images/BRP-Digital_and_Campaign_Logo-W-RGB.svg"
              alt="BRP"
              sx={{
                height: { xs: 44, sm: 56, md: 50 },
                width: 'auto',
                mr: 2,
              }}
            />
            <Typography
              variant="h2"
              component="span"
              sx={{
                color: '#FFD200',
                fontWeight: 900,
                gap: 1,
                textDecoration: 'none',
                fontSize: { xs: '1rem', sm: '1.3rem', md: '1.75rem' },
              }}>
              {t('common.title')}
            </Typography>
            <Typography
              variant="h3"
              component="span"
              sx={{
                color: '#fff',
                fontWeight: 500,
                fontSize: { xs: '0.85rem', sm: '1rem', md: '1.35rem' },
              }}>
              {t('common.retailEnvironmentCertification')}
            </Typography>
          </Box>
        </Box>
      </Box>
      {!isMdDown && (
        <Box className={classes.menuWrapper}>
          <Stack direction="row" className={classes.menu}>
            {menuItems.map(item => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                data-tour={item.dataTour}
                className={clsx(classes.menuButton, { [classes.menuButtonActive]: activePath === item.matchPath })}
                sx={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                <Typography variant="h3">{item.label}</Typography>
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      <Box className={classes.rightSection}>
        <Box className={classes.controls}>
          {showBackToPortalInHeader && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => history.push('/dealer/home-page')}
              sx={{
                color: theme.palette.secondary.main,
                borderColor: theme.palette.secondary.main,
                '&:hover': {
                  borderColor: theme.palette.secondary.main,
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                },
              }}>
              {t('certNav.backToPortal')}
            </Button>
          )}

          <Tooltip title={certThemeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton
              size="small"
              onClick={() => window.dispatchEvent(new Event('cert-theme-toggle-request'))}
              sx={{
                color: theme.palette.common.white,
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                p: isSmDown ? 0.75 : 1,
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.08),
                },
              }}>
              {certThemeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <LanguageSwitcher />
          <Tooltip title={t('certNav.account')}>
            <IconButton
              id="cert-account-menu-button"
              size="small"
              onClick={handleAccountMenuToggle}
              aria-label="account menu"
              aria-controls="cert-account-menu"
              aria-haspopup="true"
              aria-expanded={isAccountMenuOpen ? 'true' : undefined}
              sx={{
                color: theme.palette.common.black,
                border: `1px solid ${alpha(theme.palette.common.black, 0.2)}`,
                p: isSmDown ? 0.75 : 1,
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.black, 0.08),
                },
              }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.main,
                  color: theme.palette.common.black,
                  fontWeight: 700,
                  width: isSmDown ? 32 : 40,
                  height: isSmDown ? 32 : 40,
                }}
              />
            </IconButton>
          </Tooltip>
          <Menu
            id="cert-account-menu"
            anchorEl={accountMenuAnchorEl}
            open={isAccountMenuOpen}
            onClose={handleAccountMenuClose}
            keepMounted
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{ 'aria-labelledby': 'cert-account-menu-button' }}>
            <MenuItem onClick={handleAccountNavigation}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('certNav.account')} />
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('common.logout')} />
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Toolbar>
  );
};

export default CertificationToolHeader;
